import * as React from 'react';

/** Pixel tolerance to treat near-edge positions as fully scrolled */
const SCROLL_EDGE_THRESHOLD_PX = 1;

export type ScrollDirection = 'left' | 'right';

export interface IUseHorizontalScrollResult {
	containerRef: React.RefObject<HTMLDivElement>;
	canScrollLeft: boolean;
	canScrollRight: boolean;
	/**
	 * Vertical correction (px) for the container's top/bottom padding asymmetry.
	 * Apply as a relative `top` offset to a flex-centered arrow so it aligns to the
	 * items' center rather than the padded box center.
	 */
	verticalOffset: number;
	scrollTo: (direction: ScrollDirection) => void;
}

/**
 * Tracks the horizontal scroll position of a container and exposes whether it can
 * be scrolled further in either direction, plus a smooth scroll handler. Also
 * derives a vertical correction from the container's padding so navigation arrows
 * centered against the row stay aligned to the items even when the container has
 * asymmetric vertical padding. State is recomputed on scroll, on container resize,
 * and whenever `itemsCount` changes so arrows stay in sync as content is added.
 */
export const useHorizontalScroll = (itemsCount: number): IUseHorizontalScrollResult => {
	const containerRef = React.useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = React.useState(false);
	const [canScrollRight, setCanScrollRight] = React.useState(false);
	const [verticalOffset, setVerticalOffset] = React.useState(0);

	const updateScrollState = React.useCallback(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		const maxScrollLeft = node.scrollWidth - node.clientWidth;
		setCanScrollLeft(node.scrollLeft > SCROLL_EDGE_THRESHOLD_PX);
		setCanScrollRight(node.scrollLeft < maxScrollLeft - SCROLL_EDGE_THRESHOLD_PX);

		const styles = window.getComputedStyle(node);
		const paddingTop = parseFloat(styles.paddingTop) || 0;
		const paddingBottom = parseFloat(styles.paddingBottom) || 0;
		setVerticalOffset((paddingTop - paddingBottom) / 2);
	}, []);

	React.useEffect(() => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		updateScrollState();
		node.addEventListener('scroll', updateScrollState, { passive: true });

		const resizeObserver = new ResizeObserver(updateScrollState);
		resizeObserver.observe(node);

		return () => {
			node.removeEventListener('scroll', updateScrollState);
			resizeObserver.disconnect();
		};
	}, [updateScrollState, itemsCount]);

	const scrollTo = React.useCallback((direction: ScrollDirection) => {
		const node = containerRef.current;
		if (!node) {
			return;
		}

		const firstItem = node.firstElementChild;
		const gap = parseFloat(window.getComputedStyle(node).columnGap) || 0;
		const distance = firstItem instanceof HTMLElement ? firstItem.offsetWidth + gap : node.clientWidth;
		node.scrollBy({ left: direction === 'left' ? -distance : distance, behavior: 'smooth' });
	}, []);

	return { containerRef, canScrollLeft, canScrollRight, verticalOffset, scrollTo };
};
